import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '../components/Tooltip';
import { API_CONFIG } from '../config/apiConfig';

// API response interfaces based on the OpenAPI specification
interface ApiQuizQuestion {
  question: string;
  options: string[];
  answer: string;
}

interface ApiFlashcard {
  front: string;
  back: string;
}

interface StudyAidsResponse {
  quiz: ApiQuizQuestion[];
  flashcards: ApiFlashcard[];
}

// Local state interfaces
interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: string;
}

interface Flashcard {
  front: string;
  back: string;
}

interface GeneratedMaterials {
  quizQuestions: QuizQuestion[];
  flashcards: Flashcard[];
}

const TextToStudyAidsPage: React.FC = () => {
  const navigate = useNavigate();
  const [inputText, setInputText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLaunched, setIsLaunched] = useState<boolean>(false);
  const [generatedMaterials, setGeneratedMaterials] = useState<GeneratedMaterials | null>(null);
  const [showAnswers, setShowAnswers] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);

  // Mock data for demonstration
  const mockGeneratedMaterials: GeneratedMaterials = {
    quizQuestions: [
      {
        question: "What is the primary function of the mitochondria?",
        options: [
          "A) Protein synthesis",
          "B) Cellular respiration",
          "C) Waste removal",
          "D) DNA replication"
        ],
        correctAnswer: "B"
      },
      {
        question: "Which gas do plants absorb from the atmosphere?",
        options: [
          "A) Oxygen",
          "B) Nitrogen",
          "C) Carbon Dioxide",
          "D) Argon"
        ],
        correctAnswer: "C"
      }
    ],
    flashcards: [
      {
        front: "Photosynthesis",
        back: "The process used by plants, algae, and certain bacteria to harness energy from sunlight and turn it into chemical energy."
      },
      {
        front: "Osmosis",
        back: "The movement of water molecules from a solution with a high concentration of water molecules to a solution with a lower concentration, through a cell's partially permeable membrane."
      }
    ]
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
  };
  
  const handleClear = () => {
    setInputText('');
    setIsLaunched(false);
    setGeneratedMaterials(null);
    setProgress(0);
  };

  const handleGenerateClick = async () => {
    if (!inputText.trim()) return;
    
    setIsLoading(true);
    setProgress(0);
    console.log('Generating study aids for text:', inputText.substring(0, 50) + '...');
    
    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + Math.random() * 15;
          return newProgress >= 90 ? 90 : newProgress;
        });
      }, 500);
      
      // Use the API_CONFIG to get the base URL
      const apiUrl = `${API_CONFIG.BASE_URL}/api/study-aid/generate`;
      console.log('Calling API at:', apiUrl);
      
      // Make the API call
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inputText),
      });
      
      // Clear the progress interval
      clearInterval(progressInterval);
      
      console.log('API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`API request failed with status ${response.status}: ${errorText}`);
      }
      
      // Set progress to 100% when we get a response
      setProgress(100);
      
      const data: StudyAidsResponse = await response.json();
      console.log('API response data:', data);
      
      // Transform API response to our local state format
      const transformedData: GeneratedMaterials = {
        quizQuestions: data.quiz.map(q => ({
          question: q.question,
          options: q.options,
          correctAnswer: q.answer
        })),
        flashcards: data.flashcards.map(f => ({
          front: f.front,
          back: f.back
        }))
      };
      
      console.log('Transformed data:', transformedData);
      setGeneratedMaterials(transformedData);
      setIsLaunched(true);
    } catch (error) {
      console.error('Error generating study aids:', error);
      console.log('Using mock data as fallback');
      // Fallback to mock data in case of error
      setGeneratedMaterials(mockGeneratedMaterials);
      setIsLaunched(true);
      setProgress(100);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6 shadow-md">
        <div className="container mx-auto px-4">
          <button 
            className="mb-3 px-3 py-1 bg-white/20 rounded hover:bg-white/40 text-sm transition-all duration-300 active:scale-90 active:translate-x-[-4px]"
            onClick={() => navigate('/')}
          >
            ← Back
          </button>
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h1 className="text-3xl font-bold">Text-to-Study-Aids</h1>
            </div>
            <p className="text-blue-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Generate quizzes and flashcards automatically from any block of text
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div>
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Your Text:</h2>
            <textarea
              className="w-full h-64 p-4 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Paste your article, notes, or any block of text here..."
              value={inputText}
              onChange={handleTextChange}
              disabled={isLoading}
            ></textarea>
            
            <div className="mt-4 flex gap-2">
              <button
                className="flex-1 py-3 px-4 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                onClick={handleGenerateClick}
                disabled={isLoading || !inputText.trim()}
              >
                {isLoading ? 'Generating...' : 'Generate Study Guide'}
              </button>
              <button
                className="py-3 px-4 rounded-lg font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                onClick={handleClear}
                disabled={isLoading}
              >
                Clear
              </button>
            </div>
            
            {isLoading && (
              <div className="mt-3">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">{Math.round(progress)}%</p>
              </div>
            )}
          </div>
          
          <div className="mt-6">
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Generated Materials</h2>
            
            {!isLaunched ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 h-64 flex items-center justify-center">
                <p className="text-gray-500 dark:text-gray-400 text-center">
                  Your generated study materials will appear here after you generate a study guide.
                </p>
              </div>
            ) : isLoading ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 h-64 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 dark:border-blue-400"></div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white">Generated Materials</h3>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="show-answers" 
                      checked={showAnswers}
                      onChange={() => setShowAnswers(!showAnswers)}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="show-answers" className="text-sm text-gray-700 dark:text-gray-300">
                      Show Answers
                    </label>
                  </div>
                </div>
                
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Quiz Questions</h3>
                  
                  {generatedMaterials?.quizQuestions.map((question, index) => (
                    <div key={index} className="mb-6">
                      <p className="font-medium mb-2 dark:text-gray-300">{index + 1}. {question.question}</p>
                      <ul className="space-y-2">
                        {question.options.map((option, optIndex) => (
                          <li key={optIndex} className="flex items-start">
                            <span className="mr-2">•</span>
                            <span className="dark:text-gray-300">{option}</span>
                          </li>
                        ))}
                      </ul>
                      {showAnswers && (
                        <p className="text-green-600 mt-2">Correct Answer: {question.correctAnswer}</p>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Flashcards section removed as requested */}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextToStudyAidsPage;
